import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Building2, Image as ImageIcon, Upload, Trash2 } from "lucide-react";

const EMPTY = { name: "", cif_nif: "", address: "", phone: "", email: "", website: "", logo_path: "", footer: "" };

export default function CompanySettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/company")
      .then(({ data }) => setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(data || {}).map(([k, v]) => [k, v ?? ""])) }))
      .catch(() => setForm({ ...EMPTY }));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, logo_path: data.path }));
      toast.success("Logo listo. Pulsa «Guardar» para aplicarlo.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo subir el logo.");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/company", form);
      toast.success("Datos de empresa guardados.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar.");
    } finally {
      setSaving(false);
    }
  };

  if (!form) return <div className="p-10 text-muted-foreground" data-testid="company-loading">Cargando…</div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12" data-testid="company-settings-page">
      <div className="mb-8">
        <div className="caption mb-2 flex items-center gap-2"><Building2 className="h-4 w-4" /> Panel del administrador</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Datos de empresa</h1>
        <p className="text-muted-foreground mt-2">Se usan en la cabecera de los presupuestos que envías a tus clientes.</p>
      </div>

      <div className="rounded-2xl border border-border bg-card p-6 sm:p-7 space-y-5">
        <div className="space-y-2">
          <Label>Logo de la empresa</Label>
          <div className="flex items-center gap-4">
            <div className="h-20 w-32 rounded-lg border border-border bg-secondary/40 overflow-hidden flex items-center justify-center shrink-0" data-testid="company-logo-preview">
              {form.logo_path
                ? <img src={`${API}/files/${form.logo_path}`} alt="Logo" className="h-full w-full object-contain" />
                : <ImageIcon className="h-6 w-6 text-muted-foreground" />}
            </div>
            <div className="space-y-2">
              <label className="inline-flex">
                <input type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="company-logo-input" />
                <span className="inline-flex items-center gap-1.5 text-sm rounded-full border border-border px-3 py-1.5 cursor-pointer hover:bg-secondary">
                  <Upload className="h-4 w-4" /> {uploading ? "Subiendo…" : (form.logo_path ? "Cambiar logo" : "Subir logo")}
                </span>
              </label>
              {form.logo_path && (
                <Button type="button" variant="ghost" size="sm" className="ml-2 text-destructive hover:text-destructive gap-1.5" onClick={() => setForm((f) => ({ ...f, logo_path: "" }))} data-testid="company-logo-remove">
                  <Trash2 className="h-4 w-4" /> Quitar
                </Button>
              )}
              <p className="text-[11px] text-muted-foreground">PNG o JPG, máx. 8 MB. Se muestra en la cabecera del presupuesto.</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="c-name">Nombre / Razón social</Label>
            <Input id="c-name" data-testid="company-name-input" value={form.name} onChange={set("name")} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-cif">CIF / NIF</Label>
            <Input id="c-cif" data-testid="company-cif-input" value={form.cif_nif} onChange={set("cif_nif")} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="c-addr">Dirección</Label>
          <Input id="c-addr" data-testid="company-address-input" value={form.address} onChange={set("address")} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="c-phone">Teléfono</Label>
            <Input id="c-phone" data-testid="company-phone-input" value={form.phone} onChange={set("phone")} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-email">Email</Label>
            <Input id="c-email" data-testid="company-email-input" value={form.email} onChange={set("email")} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="c-web">Web</Label>
            <Input id="c-web" data-testid="company-website-input" value={form.website} onChange={set("website")} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="c-foot">Pie: condiciones / validez de la oferta</Label>
          <Textarea id="c-foot" rows={3} data-testid="company-footer-input" value={form.footer} onChange={set("footer")}
            placeholder="Ej.: Presupuesto válido 30 días. IVA incluido. Forma de pago…" />
        </div>

        <Button onClick={save} disabled={saving} className="rounded-full h-11 px-6" data-testid="company-save-button">
          {saving ? "Guardando…" : "Guardar"}
        </Button>
      </div>
    </div>
  );
}
